import moment from 'moment';
import { ApiService } from 'api';
import { OPM } from 'common/constants/allocations';
import { NewAllocationScenario } from 'common/types/allocation';
import { ValuationsApproach } from 'common/types/valuation';
import { DISPLAY_DATE_FORMAT } from 'pages/Financials/data';
import { ALLOCATION_SCENARIO_METHOD_OPM } from 'pages/ValuationsAllocation/common/constants/allocation';
import { dbShortDate, getNumberValue, getObjectValue } from 'utilities';

export const refreshAllOpmRiskFreeRates = async (params: {
  approaches: ValuationsApproach[];
  allocationScenarios: NewAllocationScenario[];
  marketDate: string | Date | null | undefined;
}): Promise<{
  approaches: ValuationsApproach[];
  allocationScenarios: NewAllocationScenario[];
}> => {
  const { approaches, allocationScenarios, marketDate } = params;
  const fallbackDate = marketDate ? dbShortDate(marketDate) : null;
  const rateCache = new Map<string, number>();

  const lookupRate = async (maturity: number | string | null | undefined, rateDate: string | null) => {
    if (maturity == null || maturity === '' || !rateDate || Number(maturity) <= 0) {
      return null;
    }
    const key = `${maturity}|${rateDate}`;
    if (rateCache.has(key)) {
      return rateCache.get(key) as number;
    }
    const rate = await ApiService.apiObservationsRead(
      String(maturity),
      moment(rateDate).format(DISPLAY_DATE_FORMAT)
    );
    if (rate == null) {
      return null;
    }
    const numericRate = Number(getNumberValue(rate).toFixed(6));
    rateCache.set(key, numericRate);
    return numericRate;
  };

  const refreshedApproaches = await Promise.all(
    approaches.map(async approach => {
      const backsolve = approach.valuations_approach_backsolve;
      if (backsolve) {
        const methodologies = getObjectValue(backsolve).applied_methodologies ?? [];
        const hasOpm = methodologies.some(
          methodology => Number(methodology.allocation_method) === Number(OPM)
        );
        if (!hasOpm) {
          return approach;
        }
        const rateDate = backsolve.opm_backsolve_date
          ? dbShortDate(backsolve.opm_backsolve_date)
          : fallbackDate;
        const approachRate = await lookupRate(backsolve.maturity, rateDate);
        return {
          ...approach,
          valuations_approach_backsolve: {
            ...backsolve,
            ...(approachRate == null ? {} : { risk_free_rate: approachRate }),
          },
        } as ValuationsApproach;
      }

      const futureExit = approach.valuations_approach_future_exit;
      if (futureExit && Number(futureExit.allocation_method) === Number(ALLOCATION_SCENARIO_METHOD_OPM)) {
        const rate = await lookupRate(futureExit.maturity, fallbackDate);
        return rate == null
          ? approach
          : ({
            ...approach,
            valuations_approach_future_exit: { ...futureExit, risk_free_rate: rate },
          } as ValuationsApproach);
      }

      return approach;
    })
  );

  const refreshedScenarios = await Promise.all(
    allocationScenarios.map(async scenario => {
      if (Number(scenario.scenario_method) !== Number(ALLOCATION_SCENARIO_METHOD_OPM)) {
        return scenario;
      }
      const rate = await lookupRate(scenario.maturity, fallbackDate);
      return rate == null ? scenario : { ...scenario, risk_free_rate: rate };
    })
  );

  return { approaches: refreshedApproaches, allocationScenarios: refreshedScenarios };
};
